import Vue from 'vue';
import axios from 'axios';

const axiosIns = axios.create({
  baseURL: process.env.VUE_APP_BACKEND_URL,
  withCredentials: true,
  withXSRFToken: true
});

function readCsrfFromDocumentCookie() {
  if (typeof document === 'undefined') {
    return null
  }
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

function getCsrfTokenSync() {
  if (window.$cookies) {
    const fromPlugin = window.$cookies.get('XSRF-TOKEN')
    if (fromPlugin) {
      return fromPlugin
    }
  }
  return readCsrfFromDocumentCookie()
}

// Short retry only when a mutating request needs the token and it is not ready yet.
function getCsrfTokenWithRetry(attempts = 3, delay = 50) {
  return new Promise((resolve) => {
    function check(attempt) {
      const csrfToken = getCsrfTokenSync()
      if (csrfToken) {
        resolve(csrfToken)
        return
      }
      if (attempt < attempts) {
        setTimeout(() => check(attempt + 1), delay)
        return
      }
      resolve(null)
    }
    check(0)
  })
}

axiosIns.interceptors.request.use(async (config) => {
  const method = String(config.method || 'get').toLowerCase()
  const isSafeMethod = method === 'get' || method === 'head' || method === 'options'

  // Never block safe/boot requests on CSRF cookie retries (was causing ~2s dead time before api/user).
  let csrfToken = getCsrfTokenSync()
  if (!csrfToken && !isSafeMethod && !config.skipCsrfWait) {
    csrfToken = await getCsrfTokenWithRetry()
  }
  if (csrfToken) {
    config.headers['X-XSRF-TOKEN'] = csrfToken
  }

  if (window.companyId) {
    config.headers['X-Company-Id'] = window.companyId
  }

  return config
});

axiosIns.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    if ((status === 401 || status === 419) && typeof window !== 'undefined') {
      if (error?.config?.skipSessionRedirect) {
        return Promise.reject(error)
      }

      const path = window.location?.pathname || ''
      const message = String(error?.response?.data?.message || '')
      const isBlocked = message.toLowerCase().includes('blocked')
      const onLoginPage = path.includes('/login')
      // Lazy require avoids circular import with the auth store (which uses this axios instance).
      // Anonymous visitors get 401 from api/user on boot — that is expected, not a session expiry.
      let wasAuthenticated = false
      try {
        // eslint-disable-next-line global-require
        const store = require('@/store').default
        wasAuthenticated = Boolean(store.getters['auth/authenticated'])
      } catch (e) {
        wasAuthenticated = false
      }

      if (!onLoginPage && wasAuthenticated) {
        import('@/libs/sessionWatch').then(({ forceSessionExpiredRedirect }) => {
          forceSessionExpiredRedirect({ blocked: isBlocked })
        }).catch(() => {
          // Fallback only for protected pages if sessionWatch fails to load.
          try {
            // eslint-disable-next-line global-require
            const { isPublicAuthPath } = require('@/libs/sessionWatch')
            if (isPublicAuthPath(path)) {
              return
            }
          } catch (e) {
            // ignore
          }
          if (!path.includes('/login')) {
            window.location.assign(isBlocked ? '/login?blocked=1' : '/login')
          }
        })
      }
    }

    return Promise.reject(error)
  },
);

export default axiosIns;
